import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Smartphone,
  Lock,
  User,
  ArrowRight,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { redirectAfterLogin, getRedirectUrl } from '../utils/authRedirect'
import api from '../utils/api'

function Login() {
  const [loginType, setLoginType] = useState('code') // 'code' | 'password'
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const navigate = useNavigate()
  const { login, fetchUser, user, loading: authLoading } = useAuth()

  // 如果已经登录，直接跳转
  useEffect(() => {
    if (!authLoading && user) {
      redirectAfterLogin(navigate, '/')
    }
  }, [user, authLoading, navigate])

  // 调试：页面加载时输出当前 URL 信息
  useEffect(() => {
    console.log('[Login] Component mounted:', {
      href: window.location.href,
      search: window.location.search,
      redirectUrl: getRedirectUrl()
    })
  }, [])

  const validatePhone = (phone) => {
    return /^1[3-9]\d{9}$/.test(phone)
  }

  const sendCode = async () => {
    if (!validatePhone(phone)) {
      setError('请输入有效的手机号')
      return
    }

    setSending(true)
    setError('')

    try {
      const response = await api.post('/auth/send-code', { phone })
      if (response.data.success) {
        setSuccess('验证码已发送')
        setCountdown(60)

        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer)
              return 0
            }
            return prev - 1
          })
        }, 1000)

        // 注释掉前台显示开发环境验证码的逻辑，保持页面整洁和正式
        /*
        if (response.data.debugCode) {
          setTimeout(() => {
            setSuccess(`验证码: ${response.data.debugCode} (开发环境)`)
          }, 1000)
        }
        */
      }
    } catch (error) {
      setError(error.response?.data?.error || '发送验证码失败')
    } finally {
      setSending(false)
    }
  }

  const handleCodeLogin = async (e) => {
    e.preventDefault()

    if (!validatePhone(phone)) {
      setError('请输入有效的手机号')
      return
    }

    if (!code || code.length !== 6) {
      setError('请输入6位验证码')
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await login(phone, code)
      setSuccess('登录成功！')
      // 跳转逻辑已交由上方的 useEffect 处理
    } catch (error) {
      setError(error.message || '登录失败')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordLogin = async (e) => {
    e.preventDefault()

    if (!username.trim()) {
      setError('请输入用户名')
      return
    }

    if (!password) {
      setError('请输入密码')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await api.post('/auth/login-password', {
        username: username.trim(),
        password
      })

      if (response.data.success) {
        // 保存token到localStorage
        localStorage.setItem('token', response.data.token)
        
        // 更新AuthContext中的用户状态
        // 这里我们需要手动设置用户，因为login方法只支持验证码登录
        // 所以我们直接调用fetchUser来获取用户信息
        await fetchUser()

        setSuccess('登录成功！')
        // 跳转逻辑已交由上方的 useEffect 处理
      } else {
        setError(response.data.error || '用户名或密码错误')
      }
    } catch (error) {
      setError(error.response?.data?.error || '登录失败')
    } finally {
      setLoading(false)
    }
  }

  if (user && !authLoading) {
    return null // 如果已登录，在重定向之前不渲染表单，避免闪烁
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-dclaw-500 to-dclaw-700 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <span className="text-white font-bold text-xl sm:text-2xl">D</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              欢迎回来
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              {loginType === 'code' ? '使用手机号验证码登录' : '使用账号密码登录'}
            </p>
          </div>

          {/* Login Type Toggle */}
          <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-700 rounded-xl mb-5 sm:mb-6">
            <button
              type="button"
              onClick={() => setLoginType('code')}
              className={`flex-1 py-2.5 sm:py-2 px-3 sm:px-4 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                loginType === 'code'
                  ? 'bg-white dark:bg-gray-600 text-dclaw-600 dark:text-dclaw-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              验证码登录
            </button>
            <button
              type="button"
              onClick={() => setLoginType('password')}
              className={`flex-1 py-2.5 sm:py-2 px-3 sm:px-4 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                loginType === 'password'
                  ? 'bg-white dark:bg-gray-600 text-dclaw-600 dark:text-dclaw-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              密码登录
            </button>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">{success}</span>
            </div>
          )}

          {/* Code Login Form */}
          {loginType === 'code' && (
            <form onSubmit={handleCodeLogin} className="space-y-5 sm:space-y-6">
              {/* Phone Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  手机号
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="请输入手机号"
                    className="w-full pl-11 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white transition-all text-sm sm:text-base min-h-[44px]"
                  />
                </div>
              </div>

              {/* Code Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  验证码
                </label>
                <div className="flex gap-2 sm:gap-3">
                  <div className="relative flex-1">
                    <Lock className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="请输入验证码"
                      className="w-full pl-11 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white transition-all text-sm sm:text-base min-h-[44px]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={sendCode}
                    disabled={countdown > 0 || sending || !validatePhone(phone)}
                    className="px-3 sm:px-4 py-2.5 sm:py-3 bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dclaw-200 dark:hover:bg-dclaw-900/50 transition-colors min-w-[100px] sm:min-w-[120px] text-sm sm:text-base whitespace-nowrap"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : countdown > 0 ? (
                      `${countdown}秒`
                    ) : (
                      '获取验证码'
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !validatePhone(phone) || code.length !== 6}
                className="w-full py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base min-h-[44px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    登录中...
                  </>
                ) : (
                  <>
                    登录
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Password Login Form */}
          {loginType === 'password' && (
            <form onSubmit={handlePasswordLogin} className="space-y-5 sm:space-y-6">
              {/* Username Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  用户名
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="请输入用户名"
                    className="w-full pl-11 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white transition-all text-sm sm:text-base min-h-[44px]"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="请输入密码"
                    className="w-full pl-11 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white transition-all text-sm sm:text-base min-h-[44px]"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !username.trim() || !password}
                className="w-full py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base min-h-[44px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    登录中...
                  </>
                ) : (
                  <>
                    登录
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </>
                )}
              </button>
            </form>
          )}


        </div>
      </div>
    </div>
  )
}

export default Login
